
import { RetailItem, UserProfile, OutfitLayer, SavedOutfit, AgentRole, AgentResponse, AgentAction, OrchestratorContext } from "../../Shared/types";
import { SchemaType, ChatSession } from "@google/generative-ai";
import { ai, STYLIST_SYSTEM_INSTRUCTION, buildPersonalizationContext } from "@shared/services/gemini-core";
import { GEMINI_CONFIG } from "../config";
import { findSimilarItems, getFashionAdvice } from "./geminiService";

// Uses the shared 'ai' instance initialized with API_KEYS.gemini

const agentTools = [
    {
        name: 'open_color_picker',
        description: 'Opens the color picker UI so the user can change the color of their current garment.',
        handler: async () => ({
            role: 'stylist' as AgentRole,
            text: "I've opened the color picker so you can adjust the look.",
            actions: [{ type: 'OPEN_COLOR_PICKER' as const, payload: {} }]
        })
    },
    {
        name: 'book_session',
        description: 'Books a styling session with a professional human stylist.',
        parameters: {
            type: SchemaType.OBJECT,
            properties: {
                serviceId: { type: SchemaType.STRING, description: 'The ID of the styling service' },
            },
            required: ['serviceId'],
        },
        handler: async (args: any) => ({
            role: 'booking' as AgentRole,
            text: "I'll help you get that scheduled. I'm passing you to our booking specialist.",
            actions: [{ type: 'BOOK_SESSION' as const, payload: { serviceId: args.serviceId || 'personal-styling' } }]
        })
    },
    {
        name: 'find_similar_items',
        description: 'Searches for retail items that are similar to the garments the user is currently wearing or interested in.',
        parameters: {
            type: SchemaType.OBJECT,
            properties: {
                query: { type: SchemaType.STRING, description: 'Specific search query or category' },
            }
        },
        handler: async (args: any, context: OrchestratorContext) => {
            const { currentLayer, userProfile } = context;
            const garment = currentLayer?.garments?.[0];
            const searchContext = args.query || garment?.nameKey || 'fashionable items';
            
            const statusSteps: { message: string, status: 'complete' | 'in-progress' | 'error' }[] = [
                { message: `Local: Searching retailers for ${searchContext}...`, status: 'in-progress' }
            ];
            
            const { results } = await findSimilarItems({ name: searchContext, url: garment?.url }, undefined, userProfile);

            statusSteps[0].status = 'complete';

            return {
                role: 'shopper' as AgentRole,
                text: results.length > 0
                    ? `I've found some ${searchContext} for you right here!`
                    : `I'm looking for ${searchContext} but couldn't find matches locally.`,
                actions: results.length > 0 ? [{ type: 'SHOW_PRODUCTS' as const, payload: results }] : [],
                statusSteps
            };
        }
    }
];

export class LocalAgentOrchestrator {
    private context: OrchestratorContext;
    private chatSession: ChatSession | null = null;

    constructor(context: OrchestratorContext) {
        this.context = context;
    }

    private initChatSession(forceRole?: AgentRole) {
        if (this.chatSession && !forceRole) return;

        const role = forceRole || 'stylist';
        const personalization = buildPersonalizationContext(this.context.userProfile);

        let roleInstruction = STYLIST_SYSTEM_INSTRUCTION;
        if (role === 'shopper') {
            roleInstruction = "You are an expert Personal Shopper. Your goal is to find the best retail products that match the user's style.";
        } else if (role === 'booking') {
            roleInstruction = "You are a Booking Coordinator. Help the user schedule sessions with human stylists.";
        }

        const systemInstruction = `${roleInstruction}\n\nUSER PERSONALIZATION: ${personalization}\n\nLOCAL MODE: You are running locally in the browser. You can use tools to assist the user. If they want to change color, use open_color_picker. If they want to shop, use find_similar_items. If they want to book a human consultation, use book_session. You can also 'delegate_to' another specialist.`;

        const model = ai.getGenerativeModel({
            model: GEMINI_CONFIG.FLASH_TEXT || 'gemini-1.5-flash',
            systemInstruction,
            tools: [{
                functionDeclarations: [
                    ...agentTools.map(({ name, description, parameters }) => ({ name, description, parameters })),
                    {
                        name: 'delegate_to',
                        description: 'Delegates the conversation to a specialized agent.',
                        parameters: {
                            type: SchemaType.OBJECT,
                            properties: {
                                role: { type: SchemaType.STRING, enum: ['stylist', 'shopper', 'booking'], description: 'Specialist to hand off to.' }
                            },
                            required: ['role']
                        }
                    }
                ]
            }] as any
        });

        this.chatSession = model.startChat({ history: [] }); // Local mode currently doesn't persist across reloads
    }

    async processMessage(query: string): Promise<AgentResponse> {
        this.initChatSession();

        const contextInfo = `User is wearing: ${this.context.currentLayer?.garments?.map(g => g.nameKey).join(', ') || 'Nothing'}.`;
        const prompt = `[CONTEXT: ${contextInfo}]\nUser says: ${query}`;

        try {
            const result = await this.chatSession!.sendMessage(prompt);
            const response = result.response;
            const functionCalls = response.functionCalls();

            if (functionCalls && functionCalls.length > 0) {
                const call = functionCalls[0];
                const tool = agentTools.find(t => t.name === call.name);

                if (tool) {
                    const toolResult = await tool.handler(call.args, this.context);
                    const assistantText = response.text() || '';
                    return {
                        ...toolResult,
                        text: assistantText ? `${assistantText}\n\n${toolResult.text}` : toolResult.text,
                        delegatedTo: toolResult.role !== 'stylist' ? toolResult.role : undefined
                    };
                }

                if (call.name === 'delegate_to') {
                    const newRole = (call.args as any).role as AgentRole;
                    this.initChatSession(newRole);
                    return {
                        role: newRole,
                        text: response.text() || `Handing off locally to ${newRole}.`,
                        delegatedTo: newRole
                    };
                }
            }

            return {
                role: 'stylist',
                text: response.text() || "Local Stylist here to help."
            };

        } catch (error) {
            console.error("Local Agent Orchestrator Error:", error);
            const advice = await getFashionAdvice(query, contextInfo, 'en', this.context.userProfile);
            return { role: 'stylist', text: advice };
        }
    }
}
